import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, RefreshCw, Loader2, PlugZap } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";

const KINDS = [
  { id: "text", pt: "Texto (artigos, SEO, guiões, social)", en: "Text (articles, SEO, scripts, social)" },
  { id: "image", pt: "Imagens", en: "Images" },
  { id: "tts", pt: "Voz (narração de vídeo)", en: "Voice (video narration)" },
];

function StatusBadge({ p, t }) {
  let cls = "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", label = t("Ativo", "Active");
  if (!p.has_key) { cls = "border-white/15 bg-white/5 text-muted-foreground"; label = t("Sem chave", "No key"); }
  else if (!p.enabled) { cls = "border-white/15 bg-white/5 text-muted-foreground"; label = t("Desativado", "Disabled"); }
  else if (p.last && !p.last.ok) { cls = "border-amber-500/40 bg-amber-500/10 text-amber-300"; label = t("Última tentativa falhou", "Last attempt failed"); }
  return <span data-testid={`provider-status-${p.id}`} className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

function ProviderRow({ kind, p, i, total, onMove, onToggle, onTest, testing, t, fmt }) {
  return (
    <div data-testid={`provider-${kind}-${p.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-background/40 p-3">
      <span className="w-6 text-center font-mono text-sm text-muted-foreground">{i + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold">{p.label}</span><StatusBadge p={p} t={t} /></div>
        <div className="text-xs text-muted-foreground font-mono">{p.model}</div>
        {p.last && <div className={`mt-1 text-xs break-words line-clamp-2 ${p.last.ok ? "text-emerald-300/80" : "text-amber-200/80"}`}>{fmt(p.last.at)} · {p.last.ok ? "OK" : p.last.message}</div>}
      </div>
      {kind === "text" && <Button data-testid={`provider-test-${p.id}`} size="sm" variant="secondary" className="gap-1.5" disabled={!p.has_key || testing} onClick={() => onTest(p.id)}>{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}{t("Testar", "Test")}</Button>}
      <Button data-testid={`provider-up-${kind}-${p.id}`} size="icon" variant="ghost" disabled={i === 0} onClick={() => onMove(i, -1)}><ArrowUp className="h-4 w-4" /></Button>
      <Button data-testid={`provider-down-${kind}-${p.id}`} size="icon" variant="ghost" disabled={i === total - 1} onClick={() => onMove(i, 1)}><ArrowDown className="h-4 w-4" /></Button>
      <Switch data-testid={`provider-toggle-${kind}-${p.id}`} checked={p.enabled} onCheckedChange={onToggle} />
    </div>
  );
}

export default function AIProvidersTab() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [testing, setTesting] = useState(null);
  const fmt = (s) => new Date(s).toLocaleString(lang === "pt" ? "pt-PT" : "en-GB");
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const load = async () => { try { setData((await api.get("/ai/providers")).data); } catch (e) { err(e); } };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setData(next);
    const body = Object.fromEntries(KINDS.map((k) => [k.id, next[k.id].map(({ id, enabled }) => ({ id, enabled }))]));
    try { setData((await api.put("/ai/providers", body)).data); toast.success(t("Guardado", "Saved")); } catch (e) { err(e); load(); }
  };
  const move = (kind, i, d) => { const l = [...data[kind]]; [l[i], l[i + d]] = [l[i + d], l[i]]; save({ ...data, [kind]: l }); };
  const toggle = (kind, i, v) => save({ ...data, [kind]: data[kind].map((p, j) => (j === i ? { ...p, enabled: v } : p)) });
  const test = async (id) => {
    setTesting(id);
    try {
      const { data: r } = await api.post(`/ai/providers/text/${id}/test`, {}, { timeout: 200000 });
      r.ok ? toast.success(t("Fornecedor a funcionar", "Provider working")) : toast.error(r.error);
      load();
    } catch (e) { err(e); } finally { setTesting(null); }
  };

  return (
    <div data-testid="ai-providers-tab">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h2 className="font-heading text-2xl font-bold mr-auto">{t("Fornecedores de IA", "AI providers")}</h2>
        <Button data-testid="providers-refresh-btn" size="sm" variant="secondary" className="gap-1.5" onClick={load}><RefreshCw className="h-4 w-4" />{t("Atualizar", "Refresh")}</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">{t("Cada geração tenta os fornecedores por esta ordem. Se um falhar, passa automaticamente ao seguinte. Se todos falharem, o erro completo fica nos Logs.", "Each generation tries providers in this order. If one fails it moves to the next. If all fail, the full error goes to Logs.")}</p>
      {!data ? <p className="text-muted-foreground">{t("A carregar…", "Loading…")}</p> : (
        <div className="grid gap-8">
          {KINDS.map((k) => (
            <section key={k.id} data-testid={`providers-section-${k.id}`}>
              <h3 className="mb-3 font-semibold">{lang === "pt" ? k.pt : k.en}</h3>
              <div className="grid gap-2">
                {data[k.id].map((p, i) => (
                  <ProviderRow key={p.id} kind={k.id} p={p} i={i} total={data[k.id].length} t={t} fmt={fmt} testing={testing === p.id}
                    onMove={(idx, d) => move(k.id, idx, d)} onToggle={(v) => toggle(k.id, i, v)} onTest={test} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
